import { IconChevronDown } from '@developerz/ui';
import { createMemo, createUniqueId, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { jobName } from '../../lib/card-controls';
import { tagOfJob, useCardControls } from '../../lib/card-controls-context';
import { pressMove, useCardMove } from '../../lib/card-move';
import type { UtilBox, UtilJob } from '../../lib/fleetUtilization';
import {
  epicLabel,
  JOB_TAG_LABEL,
  jobDuration,
  jobTitle,
  jobUsageView,
} from '../../lib/machine-jobs';
import { jobGrippable, jobSubject, type MoveSubject } from '../../lib/move-items';
import { ControlNotices } from './ControlNotices';
import { JobControls } from './JobControls';
import { JobUsageRow } from './JobUsageRow';
import { MoveButton } from './MoveButton';
import { MoveGrip } from './MoveGrip';
import { MoveTag } from './MoveTag';
import { MoveToMenu } from './MoveToMenu';

// One job in a capability group (#6745, prototype jobsHTML `row()`, `:2201-2212`): its
// title, its epic tag, its tag (Starting, Last known, and #6753's Pausing…, Paused by you,
// Epic paused) and how long it has run, then (#6753, prototype jobCtl `:2140`) its Pause or
// Resume and Stop icon buttons for an admin on this org's machine, and under it the Stop
// confirm, the Stop note and any refusal. The row is a button labelled "<title>, show
// details"; open, it shows "Repo <repo> · For <duration>" (and its epic) and the usage row.
//
// MOVING IT (#6771): for an admin on this org's machine, a movable job (`jobGrippable`) carries
// a grip before its title and a Move button after its controls; a mouse drags from the row
// itself, touch and pen from the grip. Move to opens under it. A job this page moved carries
// its Moving / Moved tag (#6772, `MoveTag`); the row is marked for the landing motion.

export function JobRow(props: {
  readonly job: UtilJob;
  readonly box: UtilBox;
  readonly nowMs: number;
  readonly open: boolean;
  readonly onToggle: () => void;
}): JSX.Element {
  const detailId = createUniqueId();
  const controls = useCardControls();
  const tag = () => tagOfJob(controls, props.job);
  const duration = () => jobDuration(props.job, props.nowMs);
  const epic = () => epicLabel(props.job, t);
  const move = useCardMove();
  const subject = createMemo((): MoveSubject | null => {
    if (move === null || !move.on()) return null;
    return jobGrippable(props.box, props.job, move.byTask())
      ? jobSubject(props.box, props.job, t)
      : null;
  });
  return (
    <li
      class="mission-jobs__row"
      data-open={props.open ? '' : undefined}
      data-move-row={subject()?.key}
      data-move-task={props.job.id}
      onPointerDown={(e) => {
        const s = subject();
        if (move !== null && s !== null) pressMove(move, s, e);
      }}
    >
      <div class="mission-jobs__row-main">
        <Show when={subject()}>{(s) => <MoveGrip subject={s()} />}</Show>
        <button
          type="button"
          class="mission-jobs__row-toggle"
          aria-expanded={props.open}
          aria-controls={detailId}
          aria-label={t('fleet.machines.job_row_label', { title: jobTitle(props.job, t) })}
          onClick={() => props.onToggle()}
        >
          <span class="mission-jobs__title">{jobTitle(props.job, t)}</span>
          <Show when={props.job.epic}>
            {(e) => <span class="mission-jobs__epic">{e().title}</span>}
          </Show>
          <Show when={tag()}>
            {(tg) => (
              <span
                class="mission-jobs__tag"
                classList={{ 'mission-jobs__tag--live': tg() === 'starting' }}
                data-tag={tg()}
              >
                {t(JOB_TAG_LABEL[tg()])}
              </span>
            )}
          </Show>
          <MoveTag taskId={props.job.id} />
          <IconChevronDown class="mission-jobs__chev" size="0.75rem" />
        </button>
        <Show when={duration()}>{(d) => <span class="mission-jobs__time">{d()}</span>}</Show>
        <JobControls job={props.job} />
        <Show when={subject()}>{(s) => <MoveButton subject={s()} />}</Show>
      </div>
      <Show when={subject()}>{(s) => <MoveToMenu subject={s()} />}</Show>
      <ControlNotices
        kind="job"
        id={props.job.id}
        name={jobName(props.job)}
        onConfirm={() => controls?.card.confirmJobStop(props.job)}
      />
      <Show when={props.open}>
        <div id={detailId} class="mission-jobs__detail">
          <span class="mission-jobs__facts">
            <Show when={props.job.repo}>
              {(repo) => (
                <span>
                  <span class="mission-jobs__fact-key">{t('fleet.machines.job_detail_repo')}</span>{' '}
                  <span class="mission-jobs__mono">{repo()}</span>
                </span>
              )}
            </Show>
            <Show when={epic()}>{(e) => <span>{e()}</span>}</Show>
            <Show when={duration()}>
              {(d) => (
                <span>
                  <span class="mission-jobs__fact-key">{t('fleet.machines.job_detail_for')}</span>{' '}
                  <span class="mission-jobs__mono">{d()}</span>
                </span>
              )}
            </Show>
          </span>
          <JobUsageRow view={jobUsageView(props.job, props.box, t)} />
        </div>
      </Show>
    </li>
  );
}
